/**
 * React Query hooks for the ADR 116 rubric CRUD API.
 *
 * Two parallel hook surfaces — district tier and school tier — sharing
 * the same key shape and invalidation semantics. Each write hook
 * invalidates BOTH the per-cell and per-scope lists; the API's
 * append-only history means a write returns a new id, so any consumer
 * holding the prior id needs to refetch.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDistrictRubricCells,
  createDistrictRubricCell,
  patchDistrictRubricCell,
  deleteDistrictRubricCell,
  getSchoolRubricCells,
  createSchoolRubricCell,
  patchSchoolRubricCell,
  deleteSchoolRubricCell,
  previewDistrictRubricImpact,
  previewSchoolRubricImpact,
  type RubricCell,
  type RubricCellListFilters,
  type RubricCellWritePayload,
  type RubricPreviewKey,
  type RubricPreviewResult,
} from '../../services/rubricCells.service';

export const rubricKeys = {
  all: ['rubric'] as const,
  district: (districtId: number) =>
    [...rubricKeys.all, 'district', districtId] as const,
  districtList: (districtId: number, filters: RubricCellListFilters) =>
    [...rubricKeys.district(districtId), 'list', { ...filters }] as const,
  districtPreview: (districtId: number, key: RubricPreviewKey) =>
    [...rubricKeys.district(districtId), 'preview', { ...key }] as const,
  school: (schoolId: number) =>
    [...rubricKeys.all, 'school', schoolId] as const,
  schoolList: (schoolId: number, filters: RubricCellListFilters) =>
    [...rubricKeys.school(schoolId), 'list', { ...filters }] as const,
  schoolPreview: (schoolId: number, key: RubricPreviewKey) =>
    [...rubricKeys.school(schoolId), 'preview', { ...key }] as const,
};

// =============================================================================
// District tier
// =============================================================================

export function useDistrictRubricCells(
  districtId: number | null | undefined,
  filters: RubricCellListFilters = {},
) {
  return useQuery({
    queryKey: rubricKeys.districtList(districtId ?? 0, filters),
    queryFn: () => getDistrictRubricCells(districtId as number, filters),
    enabled: !!districtId,
  });
}

export function useCreateDistrictRubricCell(districtId: number) {
  const queryClient = useQueryClient();
  return useMutation<RubricCell, Error, RubricCellWritePayload>({
    mutationFn: (body) => createDistrictRubricCell(districtId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: rubricKeys.district(districtId),
      });
    },
  });
}

export function usePatchDistrictRubricCell(districtId: number) {
  const queryClient = useQueryClient();
  return useMutation<
    RubricCell,
    Error,
    { cellId: number; body: Partial<RubricCellWritePayload> }
  >({
    mutationFn: ({ cellId, body }) =>
      patchDistrictRubricCell(districtId, cellId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: rubricKeys.district(districtId),
      });
    },
  });
}

export function useDeleteDistrictRubricCell(districtId: number) {
  const queryClient = useQueryClient();
  return useMutation<RubricCell, Error, number>({
    mutationFn: (cellId) => deleteDistrictRubricCell(districtId, cellId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: rubricKeys.district(districtId),
      });
    },
  });
}

// =============================================================================
// School tier
// =============================================================================

export function useSchoolRubricCells(
  schoolId: number | null | undefined,
  filters: RubricCellListFilters = {},
) {
  return useQuery({
    queryKey: rubricKeys.schoolList(schoolId ?? 0, filters),
    queryFn: () => getSchoolRubricCells(schoolId as number, filters),
    enabled: !!schoolId,
  });
}

export function useCreateSchoolRubricCell(schoolId: number) {
  const queryClient = useQueryClient();
  return useMutation<RubricCell, Error, RubricCellWritePayload>({
    mutationFn: (body) => createSchoolRubricCell(schoolId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: rubricKeys.school(schoolId),
      });
    },
  });
}

export function usePatchSchoolRubricCell(schoolId: number) {
  const queryClient = useQueryClient();
  return useMutation<
    RubricCell,
    Error,
    { cellId: number; body: Partial<RubricCellWritePayload> }
  >({
    mutationFn: ({ cellId, body }) =>
      patchSchoolRubricCell(schoolId, cellId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: rubricKeys.school(schoolId),
      });
    },
  });
}

export function useDeleteSchoolRubricCell(schoolId: number) {
  const queryClient = useQueryClient();
  return useMutation<RubricCell, Error, number>({
    mutationFn: (cellId) => deleteSchoolRubricCell(schoolId, cellId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: rubricKeys.school(schoolId),
      });
    },
  });
}

// =============================================================================
// Preview-impact hooks
// =============================================================================

/**
 * Preview the upper-bound user + school count for a proposed write to
 * a district-tier cell. `enabled` opts out of the fetch so consumers
 * can wire this as a lazy hover/focus affordance.
 *
 * The result is cached for 30 s — the underlying user / role membership
 * + AlertType set isn't quick to change in normal admin workflow, so
 * re-fetching on every checkbox flicker is wasteful. Mutations on the
 * district scope invalidate the preview cache via the write hooks'
 * `rubricKeys.district(...)` invalidation (preview keys nest under
 * district keys).
 */
export function useDistrictRubricPreview(
  districtId: number | null | undefined,
  key: RubricPreviewKey | null,
  options: { enabled?: boolean } = {},
) {
  const enabled =
    (options.enabled ?? true) && !!districtId && !!key;
  return useQuery<RubricPreviewResult>({
    queryKey: rubricKeys.districtPreview(districtId ?? 0, key ?? { role: 0 }),
    queryFn: () =>
      previewDistrictRubricImpact(districtId as number, key as RubricPreviewKey),
    enabled,
    staleTime: 30_000,
  });
}

export function useSchoolRubricPreview(
  schoolId: number | null | undefined,
  key: RubricPreviewKey | null,
  options: { enabled?: boolean } = {},
) {
  const enabled =
    (options.enabled ?? true) && !!schoolId && !!key;
  return useQuery<RubricPreviewResult>({
    queryKey: rubricKeys.schoolPreview(schoolId ?? 0, key ?? { role: 0 }),
    queryFn: () =>
      previewSchoolRubricImpact(schoolId as number, key as RubricPreviewKey),
    enabled,
    staleTime: 30_000,
  });
}
